'use client';

import 'dayjs/locale/ar';
import 'dayjs/locale/en';

import '@website/styles/globals.css';
import '@mantine/core/styles.layer.css';
import '@mantine/carousel/styles.layer.css';
import '@mantine/dropzone/styles.layer.css';
import '@mantine/notifications/styles.layer.css';

import { DirectionProvider, MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications, showNotification } from '@mantine/notifications';
import { theme } from '@repo/theme/theme';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { authAtom } from '@website/atoms';
import { authenticate, getToken } from '@website/server/actions/auth';
import { TreatyError } from '@website/utils/api';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useAtom } from 'jotai/react';
import { useEffect } from 'react';

dayjs.extend(isBetween);
dayjs.extend(relativeTime);

const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      onError: (e) => {
        if (e instanceof TreatyError) {
          showNotification({
            message: e.error?.message as string,
            color: 'red',
          });
        }
      },
    },
  },
});

export function ClientProviders({
  locale,
  initialDirection,
  children,
}: {
  locale: string;
  initialDirection: 'ltr' | 'rtl';
  children: React.ReactNode;
}) {
  const [, setAuth] = useAtom(authAtom);

  async function authFn() {
    try {
      const token = await getToken();
      const payload = await authenticate(token);

      if (payload) {
        setAuth({ status: 'Authenticated', payload: payload, token: token ?? null });
      } else {
        setAuth({ status: 'Unauthenticated', payload: null, token: null });
      }
    } catch {
      setAuth({ status: 'Unauthenticated', payload: null, token: null });
    }
  }

  useEffect(() => {
    authFn();
  }, []);

  dayjs.locale(locale);

  return (
    <QueryClientProvider client={queryClient}>
      <DirectionProvider initialDirection={initialDirection}>
        <MantineProvider theme={{ ...theme, defaultRadius: 'lg' }}>
          <ModalsProvider>
            {children}

            <Notifications />
          </ModalsProvider>
        </MantineProvider>
      </DirectionProvider>
    </QueryClientProvider>
  );
}
